import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';
import { sortByNewest } from '../utils/sortByNewest';

export const fetchUser = createAsyncThunk('users/fetchUser', async ({ token, userId }) => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
});

export const fetchUsers = createAsyncThunk('users/fetchUsers', async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.roles && filters.roles.length > 0) {
        params.set('roles', filters.roles.join(','));
    }
    if (filters.organizations && filters.organizations.length > 0) {
        params.set('organizations', filters.organizations.join(','));
    }
    if (filters.hasPinCode !== undefined) {
        params.set('hasPinCode', filters.hasPinCode);
    }
    if (filters.has2FA !== undefined) {
        params.set('has2FA', filters.has2FA);
    }
    if (filters.contentRestrictions && filters.contentRestrictions.length > 0) {
        params.set('contentRestrictions', filters.contentRestrictions.join(','));
    }
    if (filters.search) {
        params.set('search', filters.search);
    }
    if (filters.page) {
        params.set('page', filters.page);
    }
    if (filters.limit) {
        params.set('limit', filters.limit);
    }

    const response = await api.get(`/users?${params.toString()}`);
    return response.data;
});

export const updateUser = createAsyncThunk('users/update', async ({ userBody, userId }) => {
    const response = await api.put(`/users/${userId}`, userBody);
    return response.data;
});

export const deleteUser = createAsyncThunk('users/delete', async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
});

export const checkUsernameAvailability = createAsyncThunk('users/checkUsername', async ({ username, userId }) => {
    const params = new URLSearchParams({ username });
    if (userId) {
        params.append('userId', userId);
    }
    const response = await api.get(`/users/check-username?${params.toString()}`);
    return response.data;
});

const userSlice = createSlice({
    name: 'user',
    initialState: {
        list: [],
        status: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
        error: null,
        user: null,
        userStatus: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
        userError: null,
        usernameAvailable: null,
        usernameCheckStatus: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
        pagination: {
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            limit: 50,
            hasMore: false
        }
    },
    reducers: {
        resetUsernameCheck: (state) => {
            state.usernameAvailable = null;
            state.usernameCheckStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUsers.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                state.status = 'success';
                state.list = sortByNewest(action.payload.data || action.payload);
                state.pagination = action.payload.pagination || {
                    currentPage: 1,
                    totalPages: 1,
                    totalCount: Array.isArray(action.payload) ? action.payload.length : (action.payload.data?.length || 0),
                    limit: 50,
                    hasMore: false
                };
                state.error = null;
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'An error occurred';
            })

            .addCase(fetchUser.pending, (state) => {
                state.userStatus = 'loading';
            })
            .addCase(fetchUser.fulfilled, (state, action) => {
                state.userStatus = 'success';
                state.user = action.payload;
                state.userError = null;
            })
            .addCase(fetchUser.rejected, (state, action) => {
                state.userStatus = 'failed';
                state.userError = action.error.message || 'An error occurred';
            })

            .addCase(checkUsernameAvailability.pending, (state) => {
                state.usernameCheckStatus = 'loading';
            })
            .addCase(checkUsernameAvailability.fulfilled, (state, action) => {
                state.usernameCheckStatus = 'success';
                state.usernameAvailable = action.payload.available;
            })
            .addCase(checkUsernameAvailability.rejected, (state) => {
                state.usernameCheckStatus = 'failed';
                state.usernameAvailable = null;
            });
    },
});

export const { resetUsernameCheck } = userSlice.actions;

export default userSlice.reducer;
